import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { inr, dt } from "@/lib/api";
import { StatusBadge } from "@/components/Brand";

const statusLabel = { fixed: "Fixed fare", estimated: "Estimated fare", confirmation_required: "Fare confirmation required", unavailable: "Unavailable" };

const Option = ({ o, active, onSelect }) => {
  const ok = o.available && o.fare_status !== "unavailable";
  return (
    <button type="button" disabled={!ok || !onSelect} onClick={() => onSelect?.(o.rate_class)} data-testid={`fare-option-${o.rate_class || "none"}`}
      className={`w-full rounded-xl border p-4 text-left transition-colors ${active ? "border-[#0A192F] ring-2 ring-[#FFB703]" : "border-slate-200"} ${ok ? "hover:border-slate-400" : "cursor-not-allowed opacity-70"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-[#0A192F]">{o.category}{o.rate_class && o.rate_class !== o.category ? ` · ${o.rate_class} class` : ""}</p>
          <p className="mt-0.5 text-xs text-slate-500">{statusLabel[o.fare_status] || o.fare_status}</p>
        </div>
        <p className="text-lg font-extrabold text-[#0A192F]" data-testid={`fare-total-${o.rate_class || "none"}`}>
          {o.total_paise != null ? inr(o.total_paise) : "—"}
          {o.fare_status === "estimated" && <span className="ml-1 text-xs font-medium text-amber-700">est.</span>}
        </p>
      </div>
      {active && ok && (
        <div className="mt-3 space-y-1.5 border-t pt-3 text-xs text-slate-600">
          {o.lines?.map((l, i) => (
            <div key={i} className="flex justify-between gap-2"><span>{l.label}</span><span className="font-semibold text-slate-800">{inr(l.amount_paise)}</span></div>
          ))}
          {o.included_km != null && <p>Included: {o.included_km} km{o.included_hours ? ` / ${o.included_hours} hrs` : ""}</p>}
          {o.extra_km_rate != null && <p>Extra km: {inr(o.extra_km_rate)}/km{o.extra_hour_rate ? ` · Extra hour: ${inr(o.extra_hour_rate)}` : ""}</p>}
          {o.driver_bata_paise > 0 && <p>Driver bata included: {inr(o.driver_bata_paise)}</p>}
          {o.points_limit != null && <p>Point limit: {o.points_limit}</p>}
          {o.matched_group && <p>Area group {o.matched_group}</p>}
          {o.conditions?.map((c, i) => <p key={i} className="flex gap-1"><Info className="mt-0.5 h-3 w-3 shrink-0" />{c}</p>)}
          <p><span className="font-semibold">Excludes:</span> {o.exclusions?.join(", ")}</p>
        </div>
      )}
      {o.flags?.length > 0 && (
        <div className="mt-2 space-y-1">
          {o.flags.map((fl, i) => <p key={i} className="flex gap-1 text-xs text-amber-800"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{fl}</p>)}
        </div>
      )}
    </button>
  );
};

export const FarePreview = ({ quote, selected, onSelect }) => (
  <div data-testid="fare-preview">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm font-bold text-[#0A192F]">{quote.service_label}</p>
      <StatusBadge value={`Rate card ${quote.rate_card_version}`} testid="fare-ratecard-version" />
    </div>
    <div className="mb-3 space-y-0.5 text-xs text-slate-600">
      <p><span className="font-semibold">From:</span> {quote.request.pickup?.address}</p>
      {quote.request.drop && <p><span className="font-semibold">To:</span> {quote.request.drop.address}</p>}
      <p><span className="font-semibold">When:</span> {dt(quote.request.pickup_at_utc)}{quote.request.return_at_utc ? ` → ${dt(quote.request.return_at_utc)}` : ""}</p>
    </div>
    <div className="space-y-2">
      {quote.options.map((o) => <Option key={o.rate_class || "none"} o={o} active={selected === o.rate_class} onSelect={onSelect} />)}
    </div>
    <p className="mt-3 flex gap-1.5 text-xs text-slate-500"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />{quote.cancellation_terms}</p>
    <p className="mt-1 text-[11px] text-slate-400">Calculated {dt(quote.calculated_at)} · valid until {dt(quote.valid_until)} · Vehicle images are illustrative; exact model and number are shared after assignment.</p>
  </div>
);
